import { query, queryAgg } from '@confluence/query-preloader-tools/entry-points/graphql.ts';
import { fg } from '@confluence/feature-gating';
import { isExperimentEnabled } from '@confluence/feature-experiments';
import { AccessStatus } from '@confluence/session-data/entry-points/SiteInformationQuery.ts';
import { markErrorAsHandled } from '@confluence/graphql-error-processor/entry-points/GraphqlErrorProcessor.ts';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';

import { getIsLoomDisabled } from './getIsLoomDisabled.ts';
import { getIsLoomBetaEnabled } from './getIsLoomBetaEnabled.ts';
import type {
	LoomEntryPointVariant,
	LoomMeetingRecorderEligibility,
} from './useLoomEntryPointVariantState.tsx';
import {
	getLoomUserLegacyPermissionStatus,
	LoomTenantProvisionStatus,
	LoomUserPermissionStatus,
} from './useLoomEntryPointVariantState.tsx';
import {
	LoomUserPermissionQuery,
	getLoomUserPermissionQueryVariables,
	hasExactLoomUserPermission,
} from './loomUserPermission.ts';
import UseLoomTenantProvisionStatusQuery, {
	type useLoomTenantProvisionStatusQuery$data,
	type useLoomTenantProvisionStatusQuery$variables,
} from './__generated__/useLoomTenantProvisionStatusQuery.graphql.ts';
import UseIsLoomEntryPointsEnabledQuery, {
	type useIsLoomEntryPointsEnabledQuery$data,
	type useIsLoomEntryPointsEnabledQuery$variables,
} from './__generated__/useIsLoomEntryPointsEnabledQuery.graphql.ts';
import type {
	loomUserPermissionQuery$data,
	loomUserPermissionQuery$variables,
} from './__generated__/loomUserPermissionQuery.graphql.ts';
import { createAtlAttributionHeader } from './utils/createAtlAttributionHeader.ts';
import { isMeetingRecorderEligibilityEnabled } from './isMeetingRecorderEligibilityEnabled.ts';

declare global {
	interface Window {
		__SSR_LOOM_ENTRY_POINT__?: LoomEntryPointVariant | null;
	}
}

export type preloadRecordLoomButtonProps = {
	cloudId: string;
	activationId: string;
	accessStatus: AccessStatus;
	isLoggedIn: boolean;
	userId: string | null;
	environment: string;
};

/*
 * Preloader function to determine entrypoint variant, which follows the same logic in next/packages/loom-utils/src/useLoomEntryPointVariantState.tsx.
 * The function is replicated here because hooks do not execute during SSR (including useIsLoomDisabled/useIsLoomBetaEnabled) and there is no access to the central state
 *
 * NOTE: CHANGES TO THIS LOGIC MUST ALSO BE REPLICATED IN next/packages/loom-utils/src/useLoomEntryPointVariantState.tsx TO ENSURE THE CORRECT EXPERIENCE DURING SSR
 */

export async function preloadRecordLoomButton({
	cloudId,
	activationId,
	accessStatus,
	isLoggedIn,
	userId,
	environment,
}: preloadRecordLoomButtonProps): Promise<any> {
	const isLoomDisabled = getIsLoomDisabled({ accessStatus, isLoggedIn });
	const isBetaEnabled = getIsLoomBetaEnabled(accessStatus);

	const [isLoomEntryPointsEnabledResult, licensedProductsQueryResult] = await Promise.allSettled([
		preloadIsLoomEntryPointsEnabledQuery(cloudId, activationId, accessStatus),
		query<useLoomTenantProvisionStatusQuery$data, useLoomTenantProvisionStatusQuery$variables>({
			query: UseLoomTenantProvisionStatusQuery,
		}),
	]);

	const loomConfiguration =
		isLoomEntryPointsEnabledResult.status === 'fulfilled'
			? isLoomEntryPointsEnabledResult.value
			: { isLoomEntryPointsEnabled: true };
	const isLoomEntryPointsEnabled = loomConfiguration?.isLoomEntryPointsEnabled;
	const meetingRecorderEligibility = loomConfiguration?.meetingRecorderEligibility;
	const setPreloadedEntryPoint = (variant: LoomEntryPointVariant) => {
		window['__SSR_LOOM_ENTRY_POINT__'] = meetingRecorderEligibility
			? { ...variant, meetingRecorderEligibility }
			: variant;
	};

	if (window['__SSR_LOOM_ENTRY_POINT__']) {
		return Promise.resolve();
	}

	if (isLoomDisabled || !isLoomEntryPointsEnabled) {
		setPreloadedEntryPoint({ entryPointVariant: 'DISABLED' });
		return Promise.resolve();
	}

	if (isBetaEnabled) {
		setPreloadedEntryPoint({ entryPointVariant: 'BETA' });
		return Promise.resolve();
	}

	// For now, we have to do the Beta check first because `confluence_frontend_loom_cross_sell` is
	// not enabled on Beta sites
	if (!fg('confluence_frontend_loom_cross_sell')) {
		setPreloadedEntryPoint({ entryPointVariant: 'DISABLED' });
		return Promise.resolve();
	}

	if (licensedProductsQueryResult.status === 'fulfilled') {
		const licenseQueryData = licensedProductsQueryResult.value.data;

		const isTenantLicensed = licenseQueryData?.confluence_tenantContext?.licensedProducts.some(
			(product) => product.productKey === 'loom' && product.licenseStatus === 'ACTIVE',
		);

		const loomTenantProvisionStatus = isTenantLicensed
			? LoomTenantProvisionStatus.PROVISIONED
			: LoomTenantProvisionStatus.NOT_PROVISIONED;

		if (loomTenantProvisionStatus === LoomTenantProvisionStatus.NOT_PROVISIONED) {
			setPreloadedEntryPoint({ entryPointVariant: 'CROSS_FLOW' });

			// if entry point variant is "CROSS_FLOW" but FF is enabled, return "DISABLED"
			if (fg('confluence_frontend_loom_cross_flow_ads_opt_out')) {
				setPreloadedEntryPoint({ entryPointVariant: 'DISABLED' });
			}
			return Promise.resolve();
		}
	} else {
		setPreloadedEntryPoint({ entryPointVariant: 'CROSS_FLOW', error: true });

		// if entry point variant is "CROSS_FLOW" but FF is enabled, return "DISABLED"
		if (fg('confluence_frontend_loom_cross_flow_ads_opt_out')) {
			setPreloadedEntryPoint({ entryPointVariant: 'DISABLED' });
		}
		return Promise.resolve();
	}
	// tenant is provisioned at this point, switch to "CROSS_JOIN" and attempt permissions check
	setPreloadedEntryPoint({ entryPointVariant: 'CROSS_JOIN' });

	// PERMISSIONS CHECK
	const loomUserPermissionStatus = isExperimentEnabled(
		'confluence_frontend_loom_agg_perm_check_exp',
	)
		? await preloadLoomUserPermissionStatus(cloudId, userId, activationId)
		: await getLoomUserLegacyPermissionStatus(cloudId, userId, environment);

	if (loomUserPermissionStatus === LoomUserPermissionStatus.NOT_PERMITTED) {
		// if not permitted, entryPointVariant is already 'CROSS_JOIN' so resolve promise
		return Promise.resolve();
	}

	if (loomUserPermissionStatus === LoomUserPermissionStatus.ERROR) {
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		if (Boolean(process.env.REACT_SSR) && Boolean(process.env.BIFROST_TEMPLATE_BUILD)) {
			window['__SSR_LOOM_ENTRY_POINT__'] = null;
		} else {
			setPreloadedEntryPoint({ entryPointVariant: 'CROSS_JOIN', error: true });
		}
		return Promise.resolve();
	}

	setPreloadedEntryPoint({ entryPointVariant: 'CO_USE' });
	return Promise.resolve();
}

const preloadLoomUserPermissionStatus = async (
	cloudId: string,
	userId: string | null,
	activationId: string,
): Promise<LoomUserPermissionStatus> => {
	if (!userId) {
		return LoomUserPermissionStatus.NOT_PERMITTED;
	}

	const variables = getLoomUserPermissionQueryVariables(cloudId, userId);
	try {
		const { data, hasErrors } = await queryAgg<
			loomUserPermissionQuery$data,
			loomUserPermissionQuery$variables
		>({
			query: LoomUserPermissionQuery,
			variables,
			context: {
				headers: {
					...createAtlAttributionHeader(cloudId, activationId),
				},
			},
			errorPolicy: 'all',
		});

		if (hasErrors) {
			return LoomUserPermissionStatus.ERROR;
		}

		return hasExactLoomUserPermission(data, variables)
			? LoomUserPermissionStatus.PERMITTED
			: LoomUserPermissionStatus.NOT_PERMITTED;
	} catch (e) {
		getMonitoringClient().submitError(e, {
			attribution: Attribution.CONTENT_TYPES,
		});
		markErrorAsHandled(e);
		return LoomUserPermissionStatus.ERROR;
	}
};

const preloadIsLoomEntryPointsEnabledQuery = async (
	cloudId: string,
	activationId: string,
	accessStatus: AccessStatus,
): Promise<
	| {
			isLoomEntryPointsEnabled: boolean | undefined;
			meetingRecorderEligibility?: LoomMeetingRecorderEligibility;
	  }
	| undefined
> => {
	const shouldSkip = !(
		accessStatus === AccessStatus.LICENSED_ADMIN_ACCESS ||
		accessStatus === AccessStatus.LICENSED_USE_ACCESS
	);

	if (shouldSkip) {
		return;
	}

	const includeMeetingRecorderEligibility = isMeetingRecorderEligibilityEnabled();
	const { data, hasErrors } = await queryAgg<
		useIsLoomEntryPointsEnabledQuery$data,
		useIsLoomEntryPointsEnabledQuery$variables
	>({
		query: UseIsLoomEntryPointsEnabledQuery,
		variables: {
			cloudId,
			includeMeetingRecorderEligibility,
		},
		context: {
			headers: {
				...createAtlAttributionHeader(cloudId, activationId),
			},
		},
		errorPolicy: 'all',
	});

	if (hasErrors) {
		return { isLoomEntryPointsEnabled: true };
	}

	return {
		isLoomEntryPointsEnabled: data?.confluence_loomEntryPoints?.isLoomEntryPointsEnabled,
		meetingRecorderEligibility: includeMeetingRecorderEligibility
			? {
					meetingRecordingsAvailable: data?.loom_settings?.meetingRecordingsAvailable,
					meetingNotesEligible: data?.loom_settings?.meetingNotesEligible,
					connectedCalendars: data?.loom_connectedCalendars,
				}
			: undefined,
	};
};
